import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    SimpleGrid,
    Text,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Swiper, SwiperSlide} from '../swiper'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand} from '../../contexts/brand-context'
import {useUGCMedia} from '../../hooks/use-ugc-media'
import {getUgcCreatorDisplayLabel, getUgcBrandHandleLabel} from '../../utils/ugc-creator'
import {UGC_LIMIT_NAMES} from '../../constants'
import {getUgcLimit} from '../../utils/ugc-flags'
import {buildUgcItemPath} from '../../utils/url'
import {useInViewport} from '../../hooks/use-in-viewport'
import UgcVideoPlayer from '../ugc-video-player'
import {ChevronLeftIcon, ChevronRightIcon} from '../icons'
import {sendEvent, INTERACTION_LOCATION} from '../../utils/gtm-utils'

const DEFAULT_PDP_LIMIT = 4

const MAIN_IN_VIEW_RATIO = 0.6

// BV/Curalate rendition sets, widest-useful first. A 2:3 tile renders around
// 300-400px wide, so `large` (w/600) is the best default; the rest are
// fallbacks because BV omits renditions inconsistently per asset.
const IMAGE_RENDITIONS = ['large', 'extraLarge', 'original', 'medium', 'small']
const VIDEO_RENDITIONS = ['original', 'large', 'extraLarge', 'medium']

const shouldUseUgcCarousel = (itemCount, isDesktop) => (isDesktop ? itemCount > 4 : itemCount > 2)

const getUgcGridColumns = (itemCount, isDesktop) => (isDesktop ? 4 : Math.min(itemCount, 2))

const getRenditionLink = (source, keys) => {
    for (const key of keys) {
        const rendition = source?.[key]
        if (typeof rendition === 'string' && rendition) return rendition
        if (rendition?.link) return rendition.link
    }
    return ''
}

const getCreatorName = (item, brandId) => {
    const handle = item?.creatorHandle
    if (handle && typeof handle === 'object') {
        return getUgcCreatorDisplayLabel(handle, brandId)
    }
    if (typeof handle === 'string' && handle) return handle
    if (item?.creatorDisplayName) return item.creatorDisplayName
    return getUgcBrandHandleLabel(brandId)
}

const UgcPdpTile = ({item, brandId}) => {
    const styles = useMultiStyleConfig('UgcPdpCarousel', {})
    const intl = useIntl()
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [isHovered, setIsHovered] = useState(false)
    const {observeRef, intersectionRatio} = useInViewport({
        threshold: [0, 0.25, MAIN_IN_VIEW_RATIO, 0.75, 1]
    })
    const isPrimaryInView = intersectionRatio >= MAIN_IN_VIEW_RATIO
    const creatorName = getCreatorName(item, brandId)
    const media = item?.media || {}
    const isVideo = media.type === 'video'
    const mediaSrc = isVideo
        ? getRenditionLink(media, VIDEO_RENDITIONS)
        : getRenditionLink(media, IMAGE_RENDITIONS)
    const posterSrc = getRenditionLink(media.poster, IMAGE_RENDITIONS)

    if (!mediaSrc) return null

    const accessibleLabel = intl.formatMessage(
        {
            id: 'ugc_tile.assistive_msg.view_look',
            defaultMessage: 'View look by {creator}'
        },
        {creator: creatorName}
    )

    // Same composition as the landing page's UgcTile: media on top, creator
    // caption underneath, both inside one interactive wrapper.
    const tileBody = (
        <>
            <Box {...styles.media}>
                {isVideo ? (
                    <UgcVideoPlayer
                        src={mediaSrc}
                        poster={posterSrc}
                        isHovered={isDesktop ? isHovered : isPrimaryInView}
                    />
                ) : (
                    <Box
                        as="img"
                        src={mediaSrc}
                        alt={creatorName}
                        loading="lazy"
                        {...styles.image}
                    />
                )}
            </Box>
            <Text {...styles.creator}>{creatorName}</Text>
        </>
    )

    return (
        <Box
            {...styles.tile}
            ref={observeRef}
            data-testid="ugc-pdp-tile"
            onMouseEnter={() => isDesktop && setIsHovered(true)}
            onMouseLeave={() => isDesktop && setIsHovered(false)}
        >
            <Link
                to={buildUgcItemPath(brandId, item.id)}
                onClick={() => sendEvent('custom_click', {
                    type: 'carousel - pdp - ugc',
                    interaction_location: INTERACTION_LOCATION.UGC_CAROUSEL
                })}
                {...styles.tileLink}
                aria-label={accessibleLabel}
            >
                {tileBody}
            </Link>
        </Box>
    )
}

const UgcPdpCarousel = ({productId}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('UgcPdpCarousel', {})
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const {brandId} = useBrand()
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [swiper, setSwiper] = useState(null)
    const [atStart, setAtStart] = useState(true)
    const [atEnd, setAtEnd] = useState(false)
    const syncEdges = (instance) => {
        setAtStart(instance.isBeginning)
        setAtEnd(instance.isEnd)
    }
    const limit = getUgcLimit(appConfig, UGC_LIMIT_NAMES.PDP, site?.id, brandId, DEFAULT_PDP_LIMIT)
    const {data, isLoading, isError} = useUGCMedia(
        {placement: 'pdp', productId, limit, brand: brandId},
        {
            enabled: Boolean(productId),
            retry: 2,
            retryDelay: (attempt) => 500 * 2 ** attempt
        }
    )
    const items = Array.isArray(data?.items) ? data.items : []
    const headingId = `ugc-pdp-carousel-heading-${productId.replace(/[^a-zA-Z0-9_-]/g, '-')}`
    const shouldUseCarousel = shouldUseUgcCarousel(items.length, isDesktop)
    const gridColumns = getUgcGridColumns(items.length, isDesktop)

    if (isLoading || isError || items.length === 0) return null

    return (
        <Box
            as="section"
            role="region"
            aria-labelledby={headingId}
            {...styles.container}
            data-testid="ugc-pdp-carousel"
        >
            {/* Text, not Heading: Chakra's Heading carries a default `size` whose
             * fontSize wins over a theme `textStyle`, and `textStyle` set inside
             * a baseStyle does not resolve here. Applying it as a prop keeps the
             * brand's webH3 overrides intact. Mirrors MoreToDiscover. */}
            <Text id={headingId} as="h2" textStyle="webH3" {...styles.heading}>
                {intl.formatMessage({
                    id: 'label.ugcproduct.expended.view.back.cta',
                    defaultMessage: 'Styled By You'
                })}
            </Text>
            {shouldUseCarousel ? (
                <Box
                    {...styles.carousel}
                    role="group"
                    aria-roledescription="carousel"
                    aria-labelledby={headingId}
                >
                    <Swiper
                        freeMode
                        grabCursor
                        spaceBetween={8}
                        slidesPerView={1.35}
                        onSwiper={(instance) => {
                            setSwiper(instance)
                            syncEdges(instance)
                        }}
                        onSlideChange={syncEdges}
                        onReachBeginning={syncEdges}
                        onReachEnd={syncEdges}
                        onFromEdge={syncEdges}
                        onScroll={syncEdges}
                        onResize={syncEdges}
                        slidesOffsetBefore={16}
                        slidesOffsetAfter={16}
                        breakpoints={{
                            768: {
                                slidesPerView: 3,
                                spaceBetween: 8,
                                slidesOffsetBefore: 0,
                                slidesOffsetAfter: 0,
                                simulateTouch: false,
                                grabCursor: false
                            },
                            1024: {
                                slidesPerView: 4,
                                spaceBetween: 8,
                                slidesOffsetBefore: 0,
                                slidesOffsetAfter: 0,
                                simulateTouch: false,
                                grabCursor: false
                            }
                        }}
                    >
                        {items.map((item, index) => (
                            <SwiperSlide
                                key={item.id}
                                role="group"
                                aria-roledescription="slide"
                                aria-label={intl.formatMessage(
                                    {
                                        id: 'ugc_pdp_carousel.assistive_msg.slide_position',
                                        defaultMessage: 'Slide {current} of {total}'
                                    },
                                    {current: index + 1, total: items.length}
                                )}
                            >
                                <UgcPdpTile item={item} brandId={brandId} />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <Box
                        as="button"
                        type="button"
                        aria-label={intl.formatMessage({
                            id: 'ugc_pdp_carousel.previous',
                            defaultMessage: 'Previous looks'
                        })}
                        onClick={() => swiper?.slidePrev()}
                        left={0}
                        visibility={atStart ? 'hidden' : 'visible'}
                        aria-hidden={atStart ? 'true' : undefined}
                        tabIndex={atStart ? -1 : 0}
                        {...styles.arrow}
                        data-testid="ugc-pdp-carousel-prev"
                    >
                        <ChevronLeftIcon />
                    </Box>
                    <Box
                        as="button"
                        type="button"
                        aria-label={intl.formatMessage({
                            id: 'ugc_pdp_carousel.next',
                            defaultMessage: 'More looks'
                        })}
                        onClick={() => swiper?.slideNext()}
                        right={0}
                        visibility={atEnd ? 'hidden' : 'visible'}
                        aria-hidden={atEnd ? 'true' : undefined}
                        tabIndex={atEnd ? -1 : 0}
                        {...styles.arrow}
                        data-testid="ugc-pdp-carousel-next"
                    >
                        <ChevronRightIcon />
                    </Box>
                </Box>
            ) : (
                <SimpleGrid
                    columns={gridColumns}
                    gap={{base: '12px', lg: '8px'}}
                    role="list"
                    aria-labelledby={headingId}
                >
                    {items.map((item) => (
                        <Box key={item.id} role="listitem">
                            <UgcPdpTile item={item} brandId={brandId} />
                        </Box>
                    ))}
                </SimpleGrid>
            )}
        </Box>
    )
}

UgcPdpTile.propTypes = {
    item: PropTypes.object.isRequired,
    brandId: PropTypes.string
}

UgcPdpCarousel.propTypes = {
    productId: PropTypes.string.isRequired
}

export {getCreatorName, getUgcGridColumns, shouldUseUgcCarousel}
export default UgcPdpCarousel
